import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { IntegrationResponseDtoType } from '@/apis/backend/generated/models/integrationResponseDtoType';
import { IntegrationResponseDtoStatus } from '@/apis/backend/generated/models/integrationResponseDtoStatus';
import {
  useGoogleDriveOAuthControllerDisconnect,
  useIntegrationsControllerListIntegrations,
  useGoogleDriveFilesControllerAuthorize,
  useGoogleDriveFilesControllerSelectFiles,
  useGoogleDriveFilesControllerGetPickerToken,
  useGoogleDriveFoldersControllerTriggerSync,
  useGoogleDriveFoldersControllerGetSyncStatus,
  useGoogleDriveFilesControllerGetSelectedFiles,
} from '@/apis/backend/generated/integrations/integrations';

import {
  Box,
  Card,
  Chip,
  Stack,
  Alert,
  Button,
  Skeleton,
  Typography,
  CardContent,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';
import { requestDriveAuthCode, openDrivePicker } from '@/lib/google-picker';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/client/brand/$clientId/integrations/google-drive'
)({
  component: GoogleDriveIntegrationPage,
});

/**
 * Add images from Drive with a single Google consent.
 *
 * - Not connected yet: run the GIS code flow (one consent), exchange the code
 *   server-side for tokens (which activates the integration), and open the
 *   Picker with the returned access token.
 * - Already connected: ask the backend for a fresh Picker token minted from the
 *   stored refresh token - no Google prompt at all - then open the Picker.
 *
 * Either way the picked files are recorded and a background sync copies them
 * into the media library's Google Drive collection.
 */
function useAddFromDrive(isConnected: boolean) {
  const { t } = useTranslation('settings');
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const [isPicking, setIsPicking] = useState(false);

  const authorizeMutation = useGoogleDriveFilesControllerAuthorize();
  const pickerTokenMutation = useGoogleDriveFilesControllerGetPickerToken();
  const selectFilesMutation = useGoogleDriveFilesControllerSelectFiles({
    mutation: {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || t('googleDrive.filesAdded', 'Images added'), {
          variant: 'success',
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              typeof key === 'string' &&
              (key.includes('/google-drive') || key.includes('/integrations'))
            );
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('googleDrive.selectFilesError', 'Failed to add images'), {
          variant: 'error',
        });
      },
    },
  });

  const addFromDrive = async () => {
    setIsPicking(true);
    try {
      let accessToken: string;
      if (isConnected) {
        const res = await pickerTokenMutation.mutateAsync({ params: { clientId, orgId } });
        accessToken = res.accessToken;
      } else {
        const code = await requestDriveAuthCode();
        const res = await authorizeMutation.mutateAsync({
          data: { code },
          params: { clientId, orgId },
        });
        accessToken = res.accessToken;
      }

      const files = await openDrivePicker(accessToken);
      if (files.length === 0) return;

      await selectFilesMutation.mutateAsync({ data: { files }, params: { clientId, orgId } });
    } catch (err) {
      enqueueSnackbar(
        err instanceof Error ? err.message : t('googleDrive.pickerError', 'Google Picker failed'),
        { variant: 'error' }
      );
    } finally {
      setIsPicking(false);
    }
  };

  const isAdding =
    isPicking ||
    authorizeMutation.isPending ||
    pickerTokenMutation.isPending ||
    selectFilesMutation.isPending;

  return { addFromDrive, isAdding };
}

function GoogleDriveIntegrationPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();

  const { data: integrationsData, isLoading: integrationsLoading } =
    useIntegrationsControllerListIntegrations(
      activeOrganization?.id || '',
      activeClient?.id || '',
      {
        query: {
          enabled: !!activeOrganization?.id && !!activeClient?.id,
          staleTime: 30000,
        },
      }
    );

  const driveIntegration = integrationsData?.integrations?.find(
    (i) => i.type === IntegrationResponseDtoType.google_drive
  );

  const isConnected =
    driveIntegration?.status === IntegrationResponseDtoStatus.active ||
    driveIntegration?.status === IntegrationResponseDtoStatus.syncing;

  return (
    <Box>
      <Button
        size="small"
        startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
        onClick={() =>
          navigate({
            to: paths.dashboard.orgSettings(orgId).client.clientIntegrations(clientId),
          })
        }
        sx={{ mb: 2, textTransform: 'none' }}
      >
        {t('googleDrive.backToIntegrations', 'Back to Integrations')}
      </Button>

      <Card>
        <CardContent>
          {integrationsLoading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={24} width="60%" />
              <Skeleton variant="rounded" height={18} width="40%" />
              <Skeleton variant="rounded" height={14} width="30%" />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <IntegrationHeader isConnected={isConnected} />

              {!isConnected ? <NotConnectedState /> : <ConnectedState />}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function IntegrationHeader({ isConnected }: { isConnected: boolean }) {
  const { t } = useTranslation('settings');

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        component="img"
        src="/logos/google-drive.svg"
        alt="Google Drive"
        sx={{ width: 40, height: 40 }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6">Google Drive</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('googleDrive.headerSubtitle', 'Sync images from Google Drive to your media library')}
        </Typography>
      </Box>
      <Chip
        label={isConnected ? t('googleDrive.statusConnected', 'Connected') : t('googleDrive.statusNotConnected', 'Not Connected')}
        color={isConnected ? 'success' : 'default'}
        size="small"
        variant="outlined"
      />
    </Stack>
  );
}

function NotConnectedState() {
  const { t } = useTranslation('settings');
  const { addFromDrive, isAdding } = useAddFromDrive(false);

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {t(
          'googleDrive.notConnectedDescription',
          'Pick images straight from your Google Drive and Nexmail syncs them into your media library. You only grant access to the images you choose.'
        )}
      </Typography>
      <Button
        variant="contained"
        onClick={addFromDrive}
        disabled={isAdding}
        startIcon={
          isAdding ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Iconify icon="mdi:google-drive" width={20} />
          )
        }
      >
        {isAdding
          ? t('googleDrive.connecting', 'Connecting...')
          : t('googleDrive.addImages', 'Add images from Drive')}
      </Button>
    </Stack>
  );
}

function ConnectedState() {
  const { t } = useTranslation('settings');
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const { addFromDrive, isAdding } = useAddFromDrive(true);

  const { data: selectedData, isLoading: selectedLoading } =
    useGoogleDriveFilesControllerGetSelectedFiles(
      { clientId, orgId },
      { query: { enabled: !!orgId && !!clientId } }
    );

  const { data: syncStatus } = useGoogleDriveFoldersControllerGetSyncStatus(
    { clientId, orgId },
    { query: { enabled: !!orgId && !!clientId, refetchInterval: 15000 } }
  );

  const disconnectMutation = useGoogleDriveOAuthControllerDisconnect({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar(t('googleDrive.disconnectSuccess', 'Google Drive disconnected'), { variant: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/integrations');
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('googleDrive.disconnectError', 'Failed to disconnect'), { variant: 'error' });
      },
    },
  });

  const syncMutation = useGoogleDriveFoldersControllerTriggerSync({
    mutation: {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || t('googleDrive.syncStarted', 'Sync started'), { variant: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/google-drive');
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('googleDrive.syncError', 'Failed to trigger sync'), { variant: 'error' });
      },
    },
  });

  const handleDisconnect = () => {
    if (!window.confirm(t('googleDrive.disconnectConfirm', 'Disconnect Google Drive? Synced media will remain in your library.'))) return;
    disconnectMutation.mutate({ params: { clientId, orgId } });
  };

  const handleSync = () => {
    syncMutation.mutate({ params: { clientId, orgId } });
  };

  const selectedFiles = selectedData?.files || [];
  const hasSelectedFiles = selectedFiles.length > 0;

  return (
    <Stack spacing={3}>
      {syncStatus && (
        <SyncStatusInfo
          status={syncStatus.status}
          lastSyncAt={syncStatus.lastSyncAt}
          lastError={syncStatus.lastError}
        />
      )}

      {selectedLoading ? (
        <Skeleton variant="rounded" height={60} />
      ) : hasSelectedFiles ? (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">
              {t('googleDrive.syncedImages', 'Synced Images ({{count}})', {
                count: selectedFiles.length,
              })}
            </Typography>
            <Button
              size="small"
              onClick={handleSync}
              disabled={syncMutation.isPending}
              startIcon={
                syncMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="eva:refresh-outline" width={16} />
                )
              }
            >
              {t('googleDrive.syncNow', 'Sync Now')}
            </Button>
          </Stack>
          <Stack spacing={0.5}>
            {selectedFiles.map((f) => (
              <Stack
                key={f.fileId}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Iconify icon="eva:image-fill" width={18} sx={{ color: 'text.secondary' }} />
                <Typography variant="body2">{f.fileName}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : (
        <Alert severity="info">
          {t(
            'googleDrive.noFilesSelected',
            'No images selected yet. Choose images from your Drive to add them to your media library.'
          )}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          size="small"
          onClick={addFromDrive}
          disabled={isAdding}
          startIcon={
            isAdding ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Iconify icon="eva:plus-outline" width={16} />
            )
          }
        >
          {hasSelectedFiles
            ? t('googleDrive.addMoreImages', 'Add more images')
            : t('googleDrive.addImages', 'Add images from Drive')}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t(
            'googleDrive.pickerHint',
            'Nexmail can only access the images you select here. To sync new images later, add them the same way.'
          )}
        </Typography>
      </Box>

      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Button
          color="error"
          size="small"
          onClick={handleDisconnect}
          disabled={disconnectMutation.isPending}
          startIcon={<Iconify icon="eva:close-circle-outline" width={16} />}
        >
          {t('googleDrive.disconnectButton', 'Disconnect Google Drive')}
        </Button>
      </Box>
    </Stack>
  );
}

function SyncStatusInfo({
  status,
  lastSyncAt,
  lastError,
}: {
  status: string;
  lastSyncAt?: string | null;
  lastError?: string | null;
}) {
  const { t } = useTranslation('settings');
  const isSyncing = status === 'syncing';

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <Chip
        label={isSyncing ? t('googleDrive.statusSyncing', 'Syncing...') : t('googleDrive.statusActive', 'Active')}
        color={isSyncing ? 'warning' : 'success'}
        size="small"
        icon={
          isSyncing ? (
            <CircularProgress size={14} color="inherit" />
          ) : undefined
        }
      />
      {lastSyncAt && (
        <Typography variant="caption" color="text.secondary">
          {t('googleDrive.lastSynced', 'Last synced: {{date}}', { date: new Date(lastSyncAt).toLocaleString() })}
        </Typography>
      )}
      {lastError && (
        <Typography variant="caption" color="error.main">
          {t('googleDrive.syncErrorLabel', 'Error: {{message}}', { message: lastError })}
        </Typography>
      )}
    </Stack>
  );
}
